import React, { useState } from 'react';
import { 
  X, 
  Volume2, 
  VolumeX, 
  ShieldAlert, 
  ClipboardCheck, 
  Radio, 
  Server, 
  Smartphone,
  Save, 
  Check, 
  Play, 
  Activity,
  RefreshCw,
  Sparkles,
  Download
} from 'lucide-react';
import { UserSettings, DeviceInfo } from '../types';
import { sound } from '../sound';
import { checkRemoteVersion, CURRENT_APP_VERSION, VersionInfo } from '../utils/updateChecker';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: UserSettings;
  device: DeviceInfo;
  onSaveSettings: (newSettings: UserSettings) => void;
  onUpdateDeviceName: (name: string) => void;
  onOpenUpdateModal?: (info: VersionInfo) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  device,
  onSaveSettings,
  onUpdateDeviceName,
  onOpenUpdateModal
}) => {
  const [localSettings, setLocalSettings] = useState<UserSettings>(settings);
  const [deviceName, setDeviceName] = useState(device.deviceName);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSave = () => {
    onSaveSettings(localSettings);
    onUpdateDeviceName(deviceName);
    setSavedFeedback(true);
    setTimeout(() => {
      setSavedFeedback(false);
      onClose();
    }, 700);
  };

  const handleCheckUpdate = async () => {
    setIsCheckingUpdate(true);
    setUpdateStatus(null);
    try {
      const { hasUpdate, info } = await checkRemoteVersion();
      setIsCheckingUpdate(false);
      if (hasUpdate && info) {
        if (onOpenUpdateModal) {
          onOpenUpdateModal(info);
        } else {
          setUpdateStatus(`🚀 Update v${info.version} available!`);
        }
      } else {
        setUpdateStatus('✅ You are on the latest version!');
      }
    } catch {
      setIsCheckingUpdate(false);
      setUpdateStatus('Unable to verify update server.');
    }
  };

  const handleTestChime = () => {
    sound.playSyncSuccess();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="glass-card w-full max-w-md p-6 shadow-2xl border border-white/10 relative max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white font-display">Engine & Sync Preferences</h2>
            <p className="text-[11px] text-slate-400">Configure auto-sync, encryption, and audio cues</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3.5 py-5 text-xs md:text-sm">
          {/* Device Nickname */}
          <div>
            <label className="block text-slate-300 font-bold uppercase tracking-wider text-[11px] mb-1.5 flex items-center gap-1.5">
              <Smartphone size={14} className="text-cyan-400" />
              Device Nickname
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="glass-input w-full text-xs"
              placeholder="e.g. Work MacBook, Samsung Galaxy S24"
            />
          </div>

          {/* Auto Sync Toggle */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-[#090A12] border border-white/5 hover:border-white/10 transition-colors">
            <div className="pr-3">
              <div className="font-bold text-white flex items-center gap-1.5">
                <ClipboardCheck size={16} className="text-emerald-400" />
                Auto-Sync to OS Clipboard
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Automatically writes received clips to your system clipboard on focus.
              </p>
            </div>
            <input
              type="checkbox"
              checked={localSettings.autoSyncToClipboard}
              onChange={(e) =>
                setLocalSettings({ ...localSettings, autoSyncToClipboard: e.target.checked })
              }
              className="w-5 h-5 accent-cyan-400 cursor-pointer rounded-lg"
            />
          </div>

          {/* Procedural Audio */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-[#090A12] border border-white/5 hover:border-white/10 transition-colors">
            <div className="pr-3">
              <div className="font-bold text-white flex items-center gap-1.5">
                {localSettings.soundEnabled ? (
                  <Volume2 size={16} className="text-cyan-400" />
                ) : (
                  <VolumeX size={16} className="text-slate-500" />
                )}
                Procedural Sound Cues
                <button
                  type="button"
                  onClick={handleTestChime}
                  className="text-[10px] bg-white/5 text-cyan-300 hover:bg-white/10 px-2 py-0.5 rounded ml-2 border border-white/10 transition-colors"
                >
                  Test Chime
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Zero-asset high-tech audio feedback on incoming/outgoing sync events.
              </p>
            </div>
            <input
              type="checkbox"
              checked={localSettings.soundEnabled}
              onChange={(e) =>
                setLocalSettings({ ...localSettings, soundEnabled: e.target.checked })
              }
              className="w-5 h-5 accent-cyan-400 cursor-pointer rounded-lg"
            />
          </div>

          {/* Privacy Pause */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-[#090A12] border border-white/5 hover:border-white/10 transition-colors">
            <div className="pr-3">
              <div className="font-bold text-white flex items-center gap-1.5">
                <ShieldAlert size={16} className="text-amber-400" />
                Pause Sync (Privacy Shield)
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Temporarily pause sync when copying passwords or OTPs.
              </p>
            </div>
            <input
              type="checkbox"
              checked={localSettings.pauseSync}
              onChange={(e) =>
                setLocalSettings({ ...localSettings, pauseSync: e.target.checked })
              }
              className="w-5 h-5 accent-amber-400 cursor-pointer rounded-lg"
            />
          </div>

          {/* WebRTC Direct P2P */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-[#090A12] border border-white/5 hover:border-white/10 transition-colors">
            <div className="pr-3">
              <div className="font-bold text-white flex items-center gap-1.5">
                <Radio size={16} className="text-purple-400" />
                WebRTC P2P DataChannels
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Direct peer-to-peer data channel streaming when on same LAN (up to 1 Gbps).
              </p>
            </div>
            <input
              type="checkbox"
              checked={localSettings.enableWebRTC}
              onChange={(e) =>
                setLocalSettings({ ...localSettings, enableWebRTC: e.target.checked })
              }
              className="w-5 h-5 accent-purple-500 cursor-pointer rounded-lg"
            />
          </div>

          {/* Multi-Transport Routing Mode */}
          <div>
            <label className="block text-slate-300 font-bold uppercase tracking-wider text-[11px] mb-1.5 flex items-center gap-1.5">
              <Radio size={14} className="text-cyan-400" />
              Sync Transport Engine
            </label>
            <select
              value={localSettings.transportMode || 'auto'}
              onChange={(e) => setLocalSettings({ ...localSettings, transportMode: e.target.value as any })}
              className="glass-input w-full text-xs font-semibold bg-[#090A12] text-white"
            >
              <option value="auto">🌐 Triple-Layer Mesh (Cloudflare + Nostr + WebRTC)</option>
              <option value="cloudflare">⚡ Cloudflare Edge Hibernating Relay Only</option>
              <option value="nostr">📡 Decentralized Nostr Public Relays Only (No Server)</option>
            </select>
            <p className="text-[10px] text-slate-400 mt-1">
              {localSettings.transportMode === 'nostr'
                ? 'Using free public Nostr relays. 100% serverless with zero cloud hosting.'
                : localSettings.transportMode === 'cloudflare'
                ? 'Using Cloudflare Edge Worker with zero-duration WebSocket Hibernation.'
                : 'Intelligent multi-path mesh: combines Cloudflare Edge, Nostr fallback, and WebRTC P2P.'}
            </p>
          </div>

          {/* Custom Relay URL */}
          <div>
            <label className="block text-slate-300 font-bold uppercase tracking-wider text-[11px] mb-1.5 flex items-center gap-1.5">
              <Server size={14} className="text-cyan-400" />
              Custom Edge Relay URL
            </label>
            <input
              type="text"
              value={localSettings.relayUrl}
              onChange={(e) => setLocalSettings({ ...localSettings, relayUrl: e.target.value })}
              className="glass-input w-full font-mono text-xs"
              placeholder="e.g. wss://ck-share-relay.your-domain.workers.dev"
            />
          </div>

          {/* In-App Direct Update Section */}
          <div className="p-3.5 rounded-2xl bg-[#090A12] border border-white/5 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-white flex items-center gap-1.5 text-xs">
                  <Sparkles size={14} className="text-cyan-400" />
                  App Version &amp; Updates
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Currently installed: <span className="text-cyan-300 font-mono font-bold">v{CURRENT_APP_VERSION} Stable</span>
                </p>
              </div>

              <button
                type="button"
                onClick={handleCheckUpdate}
                disabled={isCheckingUpdate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-xl transition-all disabled:opacity-50"
              >
                <RefreshCw size={12} className={isCheckingUpdate ? 'animate-spin' : ''} />
                <span>{isCheckingUpdate ? 'Checking...' : 'Check Updates'}</span>
              </button>
            </div>

            {updateStatus && (
              <div className="text-[11px] text-emerald-400 font-semibold pt-1 animate-fadeIn">
                {updateStatus}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-white/10 flex items-center justify-end gap-2.5">
          <button onClick={onClose} className="btn-secondary text-xs py-2 px-4">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary text-xs py-2 px-5 shadow-lg shadow-cyan-500/25">
            {savedFeedback ? <Check size={14} /> : <Save size={14} />}
            <span>{savedFeedback ? 'Saved!' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
